import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchNewsAbout } from "services/posts.service";
import { NewsAbout } from "types/Post";

/**
 * "About" page of a company the person only visits as a group member.
 * The server sends just the public company details and the active employees
 * (name, position, work phone); members of the company keep the full page.
 */
export default function VisitorCompanyAbout() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [about, setAbout] = useState<NewsAbout | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  // Bumped by "Retry" to run the request again.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!companyId) return undefined;
    let isCurrent = true;
    setAbout(null);
    setHasFailed(false);
    fetchNewsAbout(companyId)
      .then((response) => {
        if (isCurrent) setAbout(response);
      })
      .catch(() => {
        if (isCurrent) setHasFailed(true);
      });
    return () => {
      isCurrent = false;
    };
  }, [companyId, attempt]);

  if (hasFailed) {
    return (
      <div className="mx-auto max-w-3xl p-4">
        <p className="text-red-700">{t("not_fetch")}</p>
        <button
          type="button"
          onClick={() => setAttempt((count) => count + 1)}
          className="mt-3 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700"
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  if (!about) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-4" data-testid="visitor-about-loading">
        <div className="h-8 w-1/2 animate-pulse rounded bg-gray-200" />
        <div className="h-24 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  const { company, employees } = about;
  const detailRows = [
    { label: t("about_visitor_legal_name"), value: company.legalName },
    { label: t("company_code"), value: company.code },
    { label: t("company_vat"), value: company.vat },
    { label: t("address"), value: company.address },
    { label: t("description"), value: company.description },
  ].filter((row) => row.value);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4" data-testid="visitor-about">
      <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[10rem_1fr]">
          {detailRows.map((row) => (
            <div key={row.label} className="contents">
              <dt className="text-sm font-medium text-gray-500">{row.label}</dt>
              <dd className="text-sm text-gray-900">{row.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-base font-semibold text-gray-900">
          {t("about_visitor_employees")}
        </h2>
        {employees.length === 0 ? (
          <p className="text-sm text-gray-500">{t("about_visitor_no_employees")}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {employees.map((employee) => (
              <li key={employee.id} className="flex flex-wrap items-baseline justify-between gap-x-4 py-2">
                <div>
                  <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                  {employee.position ? (
                    <div className="text-xs text-gray-500">{employee.position}</div>
                  ) : null}
                </div>
                {employee.companyPhone ? (
                  <a
                    href={`tel:${employee.companyPhone}`}
                    className="py-1 text-sm text-blue-600 hover:underline"
                    title={t("about_visitor_work_phone")}
                  >
                    {employee.companyPhone}
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
